import {
  AdAccountPlatform,
  AdAccountResource,
  AdDataStreamResource,
  DiscoveredAdAccount,
} from "@arrowsup/platform-dtos";
import { AxiosInstance } from "axios";
import { useContext, useMemo } from "react";
import { ApiContext } from "../api/api-context";

export interface AdPlatformsService {
  /** Ad accounts Arrows Up's credentials can reach on `platform`. */
  discover: (platform: AdAccountPlatform) => Promise<DiscoveredAdAccount[]>;
  /** Checks access again and refreshes the ad account's details. */
  verify: (adAccountId: number) => Promise<AdAccountResource>;
  /** Queues a sync, optionally re-pulling from `resyncFrom`. */
  sync: (
    streamId: number,
    resyncFrom?: string
  ) => Promise<AdDataStreamResource>;
}

class AdPlatformsServiceImpl implements AdPlatformsService {
  constructor(private readonly api: AxiosInstance) {}

  readonly discover = async (platform: AdAccountPlatform) =>
    (
      await this.api.get<DiscoveredAdAccount[]>(`/api/ad-accounts/discover`, {
        params: { platform },
      })
    ).data;

  readonly verify = async (adAccountId: number) =>
    (
      await this.api.post<AdAccountResource>(
        `/api/ad-accounts/${adAccountId}/verify`
      )
    ).data;

  readonly sync = async (streamId: number, resyncFrom?: string) =>
    (
      await this.api.post<AdDataStreamResource>(
        `/api/ad-data-streams/${streamId}/sync`,
        resyncFrom ? { resyncFrom } : {}
      )
    ).data;
}

export const useAdPlatformsService = (): AdPlatformsService => {
  const api = useContext(ApiContext).api;

  return useMemo(() => new AdPlatformsServiceImpl(api), [api]);
};
